import type {CarouselItemData} from '@Components/carousel/carousel.types';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {isChristmasSeason, isHalloweenSeason} from '@Utils/seasonal-promotion.util';
import type {LazyLoadingGetterParams, StockBackendData, StockGetterOptions, StockSources} from '@Libraries/pmw-stock-media-library';

export const IS_GETTY_ENABLED = true;

export enum StockPanelTabs {
  ALL = 'ALL',
  PHOTOS = 'PHOTOS',
  SHAPES = 'SHAPES',
  STICKERS = 'STICKERS',
  ICONS = 'ICONS',
  VIDEOS = 'VIDEOS',
  AUDIOS = 'AUDIO',
  TEXT_TO_IMAGE = 'TEXT_TO_IMAGE',
}

export const STOCK_PANEL_TAB_ID = 'STOCK_PANEL_TAB';

export const getAiSkeletonItem = (id: string): GridSkeletonItemStorage => {
  return {
    id: id,
    type: GRID_ITEM_TYPE.SKELETON,
    width: 180,
    height: 70,
    useFixedDimensions: false,
  };
};

export const MIN_STOCK_ITEMS_TO_DISPLAY = 9;

export enum StockButtonFilters {
  PRICING = 'Pricing',
  ANIMATION = 'Animation',
  QUALITY = 'Quality',
  GENRE = 'Genre',
  INSTRUMENT = 'Instrument',
  MOOD = 'Mood',
  SFX = 'Effects',
  TYPE = 'Type',
  CATEGORY = 'Category',
  SEASONAL = 'Seasonal',
}

export enum PricingSubFilter {
  FREE = '1',
  PREMIUM = '2',
}

export enum AnimationSubFilter {
  ANIMATED = '3',
  STILL = '4',
}

export enum QualitySubFilter {
  UHD = '5',
}

export const ICON_FILTERS_TO_CATEGORY_MAPPING: Record<string, string[]> = {
  colored: ['lineal', 'colored_fill'],
  fill: ['black_and_white_fill', 'colored_fill'],
  outline: ['outline'],
  hand_drawn: ['hand_drawn'],
};

export const SHAPE_FILTERS_TO_CATEGORY_MAPPING: Record<string, string[]> = {
  lines: ['line', 'divider', 'stroke', 'arrow'],
  basic: ['basic', 'abstract', '3d'],
  decorative: ['decorative', 'frame', 'paint', 'stroke'],
  labels: ['ribbon', 'label'],
};

export enum StockGridIDs {
  ALL = 'all-items-grid',
  PHOTOS = 'photo-items-grid',
  VIDEOS = 'video-items-grid',
  AUDIOS = 'audio-items-grid',
  SHAPES = 'shapes-items-grid',
  ICONS = 'icons-items-grid',
  STICKERS = 'stickers-items-grid',
  TEXT_TO_IMAGE = 'text-to-image-stock-grid',
}

export enum StockCarouselIDs {
  RECENTS = 'recents-carousel',
  PHOTOS = 'photos-carousel',
  STICKERS = 'stickers-carousel',
  SHAPES = 'shapes-carousel',
  VIDEOS = 'videos-carousel',
  AUDIOS = 'audios-carousel',
  ICONS = 'icons-carousel',
  TEXT_TO_IMAGE = 'text-to-image-carousel',
}

export enum InputFieldIconID {
  CANCEL = 'CANCEL',
  SEARCH = 'SEARCH',
}

export const AUDIO_CATEGORY_ID_TO_STOCK_BUTTON_FILTER: Record<string, StockButtonFilters> = {
  genre: StockButtonFilters.GENRE,
  instrument: StockButtonFilters.INSTRUMENT,
  mood: StockButtonFilters.MOOD,
  sfx: StockButtonFilters.SFX,
};

export enum StockCarouselType {
  RECENTS = 'Recents',
  PHOTOS = 'Photos',
  STICKERS = 'Stickers',
  SHAPES = 'Shapes',
  VIDEOS = 'Videos',
  AUDIOS = 'Audio',
  ICONS = 'Icons',
  TEXT_TO_IMAGE = 'TEXT_TO_IMAGE',
}

export const STOCK_TABS_TO_CAROUSEL_TYPE_MAPPING: Record<StockPanelTabs, StockCarouselType[] | undefined> = {
  [StockPanelTabs.ALL]: [StockCarouselType.RECENTS],
  [StockPanelTabs.PHOTOS]: [StockCarouselType.PHOTOS],
  [StockPanelTabs.SHAPES]: [StockCarouselType.SHAPES],
  [StockPanelTabs.STICKERS]: [StockCarouselType.STICKERS],
  [StockPanelTabs.ICONS]: [StockCarouselType.ICONS],
  [StockPanelTabs.VIDEOS]: [StockCarouselType.VIDEOS],
  [StockPanelTabs.AUDIOS]: [StockCarouselType.AUDIOS],
  [StockPanelTabs.TEXT_TO_IMAGE]: [StockCarouselType.TEXT_TO_IMAGE],
};

export const AudioFilters = [StockButtonFilters.GENRE, StockButtonFilters.INSTRUMENT, StockButtonFilters.MOOD, StockButtonFilters.SFX];

export interface CarouselData {
  [StockCarouselType.RECENTS]?: CarouselItemData[];
  [StockCarouselType.PHOTOS]?: CarouselItemData[];
  [StockCarouselType.STICKERS]?: CarouselItemData[];
  [StockCarouselType.SHAPES]?: CarouselItemData[];
  [StockCarouselType.VIDEOS]?: CarouselItemData[];
  [StockCarouselType.AUDIOS]?: CarouselItemData[];
  [StockCarouselType.ICONS]?: CarouselItemData[];
  [StockCarouselType.TEXT_TO_IMAGE]?: CarouselItemData[];
}

export type StockFilterID = StockButtonFilters;

export type StockPrimaryCTA = (selectedItems: GridItemStorage[]) => Promise<void>;

// CodeReviewUsamaAsk: Whats the difference between StockFilterID/StockButtonFilters. Why is StockFilterID used twice here |
// if, in future these filters are data driven, we'd need an id and a name. For now, i've set the both to IDs
export interface StockFilter {
  id: StockFilterID;
  type: StockFilterTypes;
  name: string;
  icon?: StockFilterIcons;
  subFilters: StockButtonSubfilter[];
}

export type StockSubfilter = StockButtonSubfilter;

export interface StockButtonSubfilter {
  text: string;
  id: string;
}

export interface SelectedFilter {
  filter: string;
  // CodeReviewUsama: Use undefined rather than null. Avoid null in typescript
  subFilter: string | null;
}

export enum StockFilterIcons {
  PRICING = 'icon-coins',
  STOCK_SOURCE = 'icon-save-loading',
  ANIMATION = 'icon-animation',
  QUALITY = 'icon-video',
  GENRE = 'icon-genre',
  INSTRUMENT = 'icon-guitar',
  MOOD = 'icon-sentiment-very-satisfied',
  SFX = 'icon-sfx',
  ELEMENTS = 'icon-elements',
}

export enum StockFilterTypes {
  BUTTON,
  DROPDOWN,
}

export const AUDIO_FILTER_ID_TO_ICON_MAPPING: Record<string, StockFilterIcons> = {
  genre: StockFilterIcons.GENRE,
  instrument: StockFilterIcons.INSTRUMENT,
  mood: StockFilterIcons.MOOD,
  sfx: StockFilterIcons.SFX,
};

export const QUALITY_SUBFILTERS: StockButtonSubfilter[] = [
  {
    text: '4K',
    id: QualitySubFilter.UHD,
  },
];

export const MOOD_SUBFILTERS: StockButtonSubfilter[] = [
  {
    text: 'Angry',
    id: '1',
  },
  {
    text: 'Serious',
    id: '2',
  },
  {
    text: 'Playful',
    id: '3',
  },
  {
    text: 'Inspiring',
    id: '4',
  },
  {
    text: 'Sad',
    id: '5',
  },
  {
    text: 'Relaxing',
    id: '6',
  },
  {
    text: 'Happy',
    id: '7',
  },
  {
    text: 'Love',
    id: '8',
  },
  {
    text: 'Epic',
    id: '124',
  },
  {
    text: 'Dark',
    id: '125',
  },
];

export const SFX_SUBFILTERS: StockSubfilter[] = [
  {
    text: 'Alarms',
    id: '12',
  },
  {
    text: 'Bells',
    id: '13',
  },
];

export const GENRE_SUBFILTERS: StockButtonSubfilter[] = [
  {
    text: 'Rock',
    id: '9',
  },
  {
    text: 'Chill Out',
    id: '10',
  },
  {
    text: 'Cinematic',
    id: '11',
  },
  {
    text: 'Country',
    id: '13',
  },
  {
    text: 'Pop',
    id: '15',
  },
  {
    text: 'Hip Hop',
    id: '20',
  },
  {
    text: 'World',
    id: '21',
  },
  {
    text: 'Corporate',
    id: '22',
  },
  {
    text: 'Holidays & Special Events',
    id: '25',
  },
  {
    text: 'Horror',
    id: '26',
  },
  {
    text: 'Jazz',
    id: '29',
  },
  {
    text: 'Kids & Family',
    id: '31',
  },
  {
    text: 'Classical',
    id: '32',
  },
  {
    text: 'Ambient',
    id: '33',
  },
  {
    text: 'Religious',
    id: '34',
  },
  {
    text: 'Blues',
    id: '36',
  },
  {
    text: 'Electronic',
    id: '37',
  },
  {
    text: 'Bumpers & Stingers',
    id: '38',
  },
  {
    text: 'R&B',
    id: '116',
  },
  {
    text: 'Folk',
    id: '18',
  },
];

export const INSTRUMENT_SUBFLITER: StockButtonSubfilter[] = [
  {
    text: 'Orchestra',
    id: '39',
  },
  {
    text: 'Guitar',
    id: '40',
  },
  {
    text: 'Brass',
    id: '41',
  },
  {
    text: 'Percussion',
    id: '42',
  },
  {
    text: 'Vocals',
    id: '43',
  },
  {
    text: 'Woodwind',
    id: '44',
  },
  {
    text: 'Piano',
    id: '45',
  },
  {
    text: 'Synth',
    id: '119',
  },
];

export enum SeasonSearchSubFilterId {
  AI = 'ai',
  Halloween = 'halloween',
  Christmas = 'christmas',
}

export const DEFAULT_APPLIED_SUBFILTER: StockSubfilter = {
  text: 'All',
  id: '0',
};

export const UHD_APPLIED_VIDEO_FILTER_ID = QualitySubFilter.UHD;

export const PRICING_SUBFILTER_FREE = PricingSubFilter.FREE;

export const getPricingSubFilter = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_free'),
      id: PricingSubFilter.FREE,
    },
    {
      text: window.i18next.t('pmwjs_getty_images'),
      id: PricingSubFilter.PREMIUM,
    },
  ];
};

export const getPricingFilter = (): StockFilter => {
  const pricingFilterNmae = window.i18next.t('pmwjs_stock_source');
  const pricingFilterIcon = StockFilterIcons.STOCK_SOURCE;
  return {type: StockFilterTypes.BUTTON, name: pricingFilterNmae, icon: pricingFilterIcon, subFilters: getPricingSubFilter(), id: StockButtonFilters.PRICING};
};

export const getAnimationSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_animated'),
      id: AnimationSubFilter.ANIMATED,
    },
    {
      text: window.i18next.t('pmwjs_still'),
      id: AnimationSubFilter.STILL,
    },
  ];
};
export const getIconSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_colored'),
      id: 'colored',
    },
    {
      text: window.i18next.t('pmwjs_fill'),
      id: 'fill',
    },
    {
      text: window.i18next.t('pmwjs_outline'),
      id: 'outline',
    },
    {
      text: window.i18next.t('pmwjs_hand_drawn'),
      id: 'hand_drawn',
    },
  ];
};

const getShapesSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_shapes_filter_lines'),
      id: 'lines',
    },
    {
      text: window.i18next.t('pmwjs_shapes_filter_basic'),
      id: 'basic',
    },
    {
      text: window.i18next.t('pmwjs_shapes_filter_decorative'),
      id: 'decorative',
    },
    {
      text: window.i18next.t('pmwjs_shapes_filter_labels'),
      id: 'labels',
    },
  ];
};

export const getSeasonalFilter = (tab: StockPanelTabs): StockFilter[] => {
  const seasonalSubFilters = [];
  if (tab === StockPanelTabs.STICKERS) {
    seasonalSubFilters.push({
      id: SeasonSearchSubFilterId.AI,
      text: window.i18next.t('pmwjs_create_with_ai'),
    });
  }
  if (isHalloweenSeason()) {
    seasonalSubFilters.push({
      id: SeasonSearchSubFilterId.Halloween,
      text: window.i18next.t('pmwjs_halloween'),
    });
  }
  if (isChristmasSeason()) {
    seasonalSubFilters.push({
      id: SeasonSearchSubFilterId.Christmas,
      text: window.i18next.t('pmwjs_christmas'),
    });
  }
  if (seasonalSubFilters.length) {
    return [
      {
        id: StockButtonFilters.SEASONAL,
        type: StockFilterTypes.BUTTON,
        name: StockButtonFilters.SEASONAL,
        subFilters: seasonalSubFilters,
      },
    ];
  }
  return [];
};

export const getStockTabsToFilterMapping = (videoQualityFiltersDisabled?: boolean): Record<StockPanelTabs, StockFilter[]> => {
  return {
    [StockPanelTabs.ALL]: [
      ...getSeasonalFilter(StockPanelTabs.ALL),
      ...(IS_GETTY_ENABLED ? [getPricingFilter()] : []),
      {
        type: StockFilterTypes.BUTTON,
        name: window.i18next.t('pmwjs_animation'),
        icon: StockFilterIcons.ANIMATION,
        subFilters: getAnimationSubFilters(),
        id: StockButtonFilters.ANIMATION,
      },
    ],
    [StockPanelTabs.PHOTOS]: [...getSeasonalFilter(StockPanelTabs.PHOTOS), ...(IS_GETTY_ENABLED ? [getPricingFilter()] : [])],
    [StockPanelTabs.SHAPES]: [
      {
        type: StockFilterTypes.BUTTON,
        name: StockPanelTabs.SHAPES,
        subFilters: getShapesSubFilters(),
        id: StockButtonFilters.CATEGORY,
      },
    ],
    [StockPanelTabs.STICKERS]: [
      ...getSeasonalFilter(StockPanelTabs.STICKERS),
      ...(IS_GETTY_ENABLED ? [getPricingFilter()] : []),
      {type: StockFilterTypes.BUTTON, name: StockButtonFilters.ANIMATION, icon: StockFilterIcons.ANIMATION, subFilters: getAnimationSubFilters(), id: StockButtonFilters.ANIMATION},
    ],
    [StockPanelTabs.ICONS]: [{type: StockFilterTypes.BUTTON, name: StockButtonFilters.CATEGORY, subFilters: getIconSubFilters(), id: StockButtonFilters.CATEGORY}],
    [StockPanelTabs.VIDEOS]: [
      ...getSeasonalFilter(StockPanelTabs.VIDEOS),
      ...(IS_GETTY_ENABLED ? [getPricingFilter()] : []),
      {
        type: StockFilterTypes.BUTTON,
        name: window.i18next.t('pmwjs_quality'),
        icon: StockFilterIcons.QUALITY,
        subFilters: videoQualityFiltersDisabled ? [] : QUALITY_SUBFILTERS,
        id: StockButtonFilters.QUALITY,
      },
    ],
    [StockPanelTabs.AUDIOS]: [
      {type: StockFilterTypes.DROPDOWN, name: window.i18next.t('pmwjs_mood'), icon: StockFilterIcons.MOOD, subFilters: MOOD_SUBFILTERS, id: StockButtonFilters.MOOD},
      {type: StockFilterTypes.DROPDOWN, name: window.i18next.t('pmwjs_sfx'), icon: StockFilterIcons.SFX, subFilters: SFX_SUBFILTERS, id: StockButtonFilters.SFX},
      {type: StockFilterTypes.DROPDOWN, name: window.i18next.t('pmwjs_genre'), icon: StockFilterIcons.GENRE, subFilters: GENRE_SUBFILTERS, id: StockButtonFilters.GENRE},
      {
        type: StockFilterTypes.DROPDOWN,
        name: window.i18next.t('pmwjs_intrument'),
        icon: StockFilterIcons.INSTRUMENT,
        subFilters: INSTRUMENT_SUBFLITER,
        id: StockButtonFilters.INSTRUMENT,
      },
    ],
    [StockPanelTabs.TEXT_TO_IMAGE]: [],
  };
};

export const STOCK_SUBFILTER_TO_ADDITIONAL_FILTERS_MAP: Record<StockPanelTabs, Record<string, Record<string, StockFilter[]>>> = {
  [StockPanelTabs.ALL]: {},
  [StockPanelTabs.PHOTOS]: {},
  [StockPanelTabs.SHAPES]: {},
  [StockPanelTabs.STICKERS]: {},
  [StockPanelTabs.ICONS]: {},
  [StockPanelTabs.VIDEOS]: {},
  [StockPanelTabs.AUDIOS]: {},
  [StockPanelTabs.TEXT_TO_IMAGE]: {},
};

export const AUDIO_CATEGORIES_REQUEST_LOCAL_STORAGE_CACHE_KEY = 'AudioCategoriesRequestCacheKey';
export const DISABLE_PIXABY_SOURCES = true;

export type GridItemBaseStorageBackendData = Record<string, any>;

export interface InternalStockParams {
  stockSources: StockSources[];
  score: number;
  offset: number;
}

export interface ExternalStockParams {
  stockSource: StockSources;
  score: number;
  offset: number;
}

export interface SourceTier {
  internalStock?: InternalStockParams;
  externalStock?: ExternalStockParams[];
}

export interface StockResponse {
  stock: StockBackendData[];
  tierResults: {
    internal: number;
    external: number[];
  }[];
  storyblocksAvailable?: boolean;
}

export type CarouselTabTiers = Partial<Record<StockPanelTabs, SourceTier[]>>;

export type StockResponseCarousel = Partial<Record<StockPanelTabs, StockBackendData[]>>;

export interface StockGetterParams extends LazyLoadingGetterParams {
  options?: StockGetterOptions;
  isManageappPage?: string;
}